import { isAxiosError } from 'axios'
import { getReaderAction } from 'apis/checkoutPaymentService'
import { cancelPaymentIntent, getPaymentIntentFromStripe } from 'apis/paymentIntent'
import { logError } from 'observability/logger'
import type { ReaderAction } from 'types/reader'
import {
	READER_ACTION_IN_PROGRESS,
	READER_ACTION_TYPE_COLLECT_INPUTS,
	STATE_REQUIRES_PAYMENT_METHOD
} from '../constants'

const PAYMENT_ALREADY_SUCCEEDED_ERROR_CODE = 'E7011'

export const READER_SEND_ERROR_OUTCOME = {
	AWAIT_RESULT: 'await_result',
	NOT_CHARGED: 'not_charged',
	UNCONFIRMED: 'unconfirmed'
} as const

export type ReaderSendErrorOutcome =
	(typeof READER_SEND_ERROR_OUTCOME)[keyof typeof READER_SEND_ERROR_OUTCOME]

const isReaderWorkingOnPayment = (
	action: ReaderAction | null,
	paymentIntentId: string
): boolean => {
	if (action?.status !== READER_ACTION_IN_PROGRESS) return false

	const paymentIntentOnReader =
		action.collect_payment_method?.payment_intent ??
		action.confirm_payment_intent?.payment_intent ??
		action.process_payment_intent?.payment_intent
	// Prompts shown before card collection carry no payment, so they count as this one
	if (paymentIntentOnReader == null) return action.type === READER_ACTION_TYPE_COLLECT_INPUTS

	return paymentIntentOnReader === paymentIntentId
}

const isAlreadySucceededError = (err: unknown): boolean =>
	isAxiosError<{ error_code?: string }>(err) &&
	err.response?.status === 409 &&
	err.response.data?.error_code === PAYMENT_ALREADY_SUCCEEDED_ERROR_CODE

/**
 * Settles a reader payment whose send errored: wait for it, or cancel it when nothing is charging it.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`) that was sent to the reader.
 * @param readerId - Stripe Terminal reader ID (`tmr_...`) it was sent to.
 * @returns `AWAIT_RESULT` when the payment moved on or the reader is still working on it,
 *   `NOT_CHARGED` once it is canceled, `UNCONFIRMED` when its state could not be checked.
 */
export const resolveReaderSendError = async (
	paymentIntentId: string,
	readerId: string
): Promise<ReaderSendErrorOutcome> => {
	try {
		// Stripe, not Postgres: the webhook that updates our row may not have landed yet
		const { data: paymentIntent } = await getPaymentIntentFromStripe(paymentIntentId)
		if (paymentIntent.status !== STATE_REQUIRES_PAYMENT_METHOD) {
			return READER_SEND_ERROR_OUTCOME.AWAIT_RESULT
		}

		const { data: reader } = await getReaderAction(readerId)
		if (isReaderWorkingOnPayment(reader.action, paymentIntentId)) {
			return READER_SEND_ERROR_OUTCOME.AWAIT_RESULT
		}

		// A prompt handoff can leave the payment with the reader app, out of Stripe's sight
		await cancelPaymentIntent(paymentIntentId)
		return READER_SEND_ERROR_OUTCOME.NOT_CHARGED
	} catch (err) {
		if (isAlreadySucceededError(err)) return READER_SEND_ERROR_OUTCOME.AWAIT_RESULT

		logError(err)
		return READER_SEND_ERROR_OUTCOME.UNCONFIRMED
	}
}
